import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  Play,
  GitPullRequest,
  MessageSquare,
  Bug,
  Square,
} from 'lucide-react';
import { Spinner } from '../design-system';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuthenticatedApi } from '../hooks/useAuthenticatedApi';
import { useSettingsModal } from '../contexts/SettingsModalContext';
import useAuthenticatedWebSocket from '../hooks/useAuthenticatedWebSocket';
import useFixMessages from '../hooks/useFixMessages';
import useWebSocketFixUrl from '../hooks/useWebSocketFixUrl';
import { apiEndpoints } from '../utils/api';
import { validationSessionKey } from '../contexts/IntegrationStatusContext';
import { OrgCodeCommitStatus } from '../types/codecommit';
import { parseCodeCommitFullName, providerLabel } from '../config/providers';
import { readNavigationOrigin, useNavigationOrigin } from '../hooks/useNavigationOrigin';
import { useQuotaBalance } from '../hooks/useQuotaBalance';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import QuotaExceededModal from '../components/common/QuotaExceededModal';
import {
  FixResponse,
  FixStatus,
  PrMergeStatus,
  DiffResponse,
  CreatePRRequest,
  CreatePRResponse,
  FixRefineRequest,
} from '../types/fix';
import toast from '../design-system/toast';
import DiffViewer from '../components/fixes/DiffViewer';
import FixActionToolbar from '../components/fixes/FixActionToolbar';
import MessageListComponent from '../components/chat/chat-area/message-display';
import { processToolCallMessages } from '../components/chat/chat-area/utils/messageHelpers';
import { convertMessagesToFrontend } from '../utils/agentUtils';
import { classifyFixEvent, isRenderableFixText, mergeFixMessageText } from './fixes/fixEvents';
import { useAutoScroll } from '../components/chat/chat-area/hooks';
import { ScrollToBottomButton as ScrollToBottomButtonComponent } from '../components/chat/chat-area/overlays';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MessageList = MessageListComponent as any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ScrollToBottomButton = ScrollToBottomButtonComponent as any;

const statusConfig: Record<FixStatus, { icon: React.ElementType; color: string; bgColor: string; label: string }> = {
  pending: { icon: Clock, color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30', label: 'Fix Pending' },
  running: { icon: Play, color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-100 dark:bg-amber-900/30', label: 'Fix Running' },
  completed: { icon: CheckCircle, color: 'text-brand-primaryDark dark:text-brand-primaryDarkMode', bgColor: 'bg-brand-primary/10 dark:bg-brand-primary/20', label: 'Fix Ready' },
  failed: { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30', label: 'Fix Failed' },
  cancelled: { icon: Square, color: 'text-ui-text-secondary', bgColor: 'bg-ui-background-tertiary', label: 'Fix Stopped' },
  pr_creation_failed: { icon: GitPullRequest, color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-100 dark:bg-amber-900/30', label: 'PR Failed' },
  pr_update_failed: { icon: GitPullRequest, color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-100 dark:bg-amber-900/30', label: 'PR Update Failed' },
};

const prStatusLabel: Record<PrMergeStatus, string> = {
  open: 'Open',
  merged: 'Merged',
  closed: 'Closed',
};

interface FrontendMessage {
  sender: string;
  content: string;
  timestamp: Date;
  uuid?: string;
  lastUpdated?: Date;
  isStreaming?: boolean;
  message_type?: string;
  tool_description?: string;
  tool_call_id?: string;
  agentType?: string;
  isSubAgent?: boolean;
  subAgentInfo?: any;
  toolStatus?: any;
  isToolCall?: boolean;
  toolCallId?: string;
}

interface AutoScrollResult {
  autoScroll: boolean;
  isNearBottom: boolean;
  scrollToBottom: (force?: boolean) => void;
  enhancedHandleScroll: (e: React.UIEvent) => void;
  handleWheelScroll: (e: React.WheelEvent) => void;
  handleScrollStart: () => void;
}

const FixDetailPage: React.FC = () => {
  const { fixId, applicationId: routeApplicationId } = useParams<{ fixId: string; applicationId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();
  const api = useAuthenticatedApi();
  const { openOrgSettings } = useSettingsModal();

  const isReadOnly = currentOrganization?.current_user_role === 'viewer';
  const isDemoOrg = !!currentOrganization?.is_demo;

  // Where this fix was opened from — the Fixes board, a lane list, or the finding it
  // addresses. Without an origin (a pasted URL or a refresh) fall back to the finding,
  // then to the findings list, which is where this page always used to return.
  const origin = readNavigationOrigin(location.state);
  // Forward links to the finding mark this fix as their origin, so the finding's own back
  // button returns here rather than jumping to the findings list.
  const fixOrigin = useNavigationOrigin('fix');

  const [fix, setFix] = useState<FixResponse | null>(null);
  const [diff, setDiff] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDiffLoading, setIsDiffLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentGenerationAttempt = fix?.current_generation_attempt ?? 1;
  const linkedFindingId = fix?.finding_ids?.[0];
  /** Back goes where the reader came from; otherwise to this fix's finding, then the list. */
  const backTo =
    origin ??
    (linkedFindingId
      ? { to: `/repositories/${routeApplicationId}/findings/${linkedFindingId}`, label: 'finding' }
      : { to: `/repositories/${routeApplicationId}/findings`, label: 'Findings' });
  const goBack = () => navigate(backTo.to);

  // PR creation state
  const [isCreatingPR, setIsCreatingPR] = useState(false);
  const [isPRPending, setIsPRPending] = useState(false);

  // Quota state
  const { quotaBalance } = useQuotaBalance();
  const { accessType } = useSubscriptionAccess();
  const [quotaModalOpen, setQuotaModalOpen] = useState(false);
  const prQuotaExhausted = quotaBalance !== null && quotaBalance.remaining_prs <= 0;
  const isFreeTierNoPRs = accessType === 'free' && quotaBalance !== null && quotaBalance.plan_prs === 0;

  // Menu state
  const [isRetrying, setIsRetrying] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRefineModalOpen, setIsRefineModalOpen] = useState(false);
  const [refinementInstructions, setRefinementInstructions] = useState('');

  // Azure DevOps PAT state
  const [isCredentialsBlocked, setIsCredentialsBlocked] = useState(false);

  // Right panel view state (toggle between activity and diff)
  const [rightPanelView, setRightPanelView] = useState<'activity' | 'diff'>('activity');

  // Message state
  const [rawMessages, setRawMessages] = useState<FrontendMessage[]>([]);
  const [expandedTools, setExpandedTools] = useState<Record<number, boolean>>({});
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Use hook for fetching messages
  const { getMessages: fetchMessages } = useFixMessages(currentOrganization?.id, fixId);

  const messages = useMemo(() => {
    return processToolCallMessages(rawMessages);
  }, [rawMessages]);

  // Auto-scroll for agent activity messages
  const agentStatus = fix?.status === 'running' ? 'generating' : 'stopped';
  const autoScroll = useAutoScroll(messagesContainerRef, messages, agentStatus) as AutoScrollResult;

  // Toggle tool expansion
  const toggleToolExpansion = useCallback((index: number) => {
    setExpandedTools((prev) => ({ ...prev, [index]: !prev[index] }));
  }, []);

  // Copy to clipboard
  const copyToClipboard = useCallback((content: string, messageId: string) => {
    navigator.clipboard.writeText(content);
    setCopiedMessageId(messageId);
    setTimeout(() => setCopiedMessageId(null), 2000);
  }, []);

  const mergeRestMessagesIntoRawMessages = useCallback((
    existingMessages: FrontendMessage[],
    restMessages: FrontendMessage[]
  ): FrontendMessage[] => {
    if (!existingMessages || existingMessages.length === 0) return restMessages;
    if (!restMessages || restMessages.length === 0) return existingMessages;

    const existingByUuid = new Map<string, FrontendMessage>();
    for (const message of existingMessages) {
      if (message.uuid) existingByUuid.set(message.uuid, message);
    }

    const merged: FrontendMessage[] = [...existingMessages];

    for (const restMessage of restMessages) {
      const uuid = restMessage.uuid;
      if (!uuid) {
        merged.push(restMessage);
        continue;
      }

      const existing = existingByUuid.get(uuid);
      if (!existing) {
        merged.push(restMessage);
        continue;
      }

      const shouldReplace =
        (existing.isStreaming && !restMessage.isStreaming) ||
        (typeof existing.content === 'string' &&
          typeof restMessage.content === 'string' &&
          restMessage.content.length > existing.content.length);

      if (shouldReplace) {
        const existingIndex = merged.findIndex((m) => m.uuid === uuid);
        if (existingIndex >= 0) {
          merged[existingIndex] = {
            ...restMessage,
            toolStatus: existing.toolStatus ?? restMessage.toolStatus,
          };
        }
      }
    }

    return merged;
  }, []);

  // Clear attempt-local UI state before loading a different fix generation attempt.
  useEffect(() => {
    setRawMessages([]);
    setExpandedTools({});
  }, [fixId, currentGenerationAttempt]);

  // Load messages when fix is loaded
  useEffect(() => {
    if (currentOrganization?.id && fixId && fix?.id) {
      let cancelled = false;

      console.log('[FixDetailPage] Fetching messages...');
      fetchMessages({ includeSubAgentMessages: true, attempt: currentGenerationAttempt }).then((data) => {
        if (cancelled) return;
        console.log('[FixDetailPage] Raw API data:', data);
        console.log('[FixDetailPage] Message count from API:', data?.length);
        if (data) {
          const converted = convertMessagesToFrontend(data, 'coding_agent');
          console.log('[FixDetailPage] Converted messages:', converted);
          console.log('[FixDetailPage] Converted count:', converted.length);
          setRawMessages((prev) => mergeRestMessagesIntoRawMessages(prev, converted));
        }
      });

      return () => {
        cancelled = true;
      };
    }
  }, [
    currentOrganization?.id,
    fixId,
    fix?.id,
    currentGenerationAttempt,
    fetchMessages,
    mergeRestMessagesIntoRawMessages,
  ]);

  const fixEventUrl = useWebSocketFixUrl(currentOrganization?.id, fixId, fix ? currentGenerationAttempt : undefined);

  const handleFixChatMessageEvent = useCallback((event: any, isDelta = false) => {
    const messageText =
      typeof event?.content === 'object' && event?.content?.text !== undefined
        ? String(event.content.text ?? '')
        : typeof event?.content === 'string'
          ? event.content
          : '';

    if (!isRenderableFixText(messageText, isDelta)) return;

    const messageUuid: string | undefined = event?.uuid || event?.id;
    const timestamp = new Date(event?.timestamp || Date.now());

    const sender =
      event?.sender === 'user'
        ? 'user'
        : event?.sender === 'system'
          ? 'system'
          : event?.sub_agent_info?.agent_name ?? 'coding_agent';

    setRawMessages((prev) => {
      if (messageUuid) {
        const existingMessageIndex = prev.findIndex((m) => m.uuid === messageUuid);
        if (existingMessageIndex >= 0) {
          const existingMessage = prev[existingMessageIndex];
          const updatedMessages = [...prev];

          updatedMessages[existingMessageIndex] = {
            ...existingMessage,
            content: mergeFixMessageText(existingMessage.content || '', messageText, isDelta),
            isStreaming: !!event?.is_streaming,
            message_type:
              event?.content?.message_type ||
              existingMessage.message_type ||
              'text',
            tool_description:
              event?.content?.tool_description ||
              existingMessage.tool_description,
            tool_call_id:
              event?.content?.tool_call_id ||
              existingMessage.tool_call_id,
            lastUpdated: new Date(),
            agentType: event?.sub_agent_info?.agent_name ?? existingMessage.agentType ?? 'coding_agent',
            isSubAgent: !!event?.sub_agent_info || existingMessage.isSubAgent,
            subAgentInfo: event?.sub_agent_info || existingMessage.subAgentInfo,
            sender,
          };

          return updatedMessages;
        }
      }

      return [
        ...prev,
        {
          sender,
          content: messageText,
          timestamp,
          uuid: messageUuid || crypto.randomUUID(),
          lastUpdated: new Date(),
          isStreaming: !!event?.is_streaming,
          message_type: event?.content?.message_type || 'text',
          tool_description: event?.content?.tool_description,
          tool_call_id: event?.content?.tool_call_id,
          agentType: event?.sub_agent_info?.agent_name ?? 'coding_agent',
          isSubAgent: !!event?.sub_agent_info,
          subAgentInfo: event?.sub_agent_info,
        },
      ];
    });
  }, []);

  const handleFixToolStreamingUpdateEvent = useCallback((event: any) => {
    const toolCallId = event?.content?.tool_call_id;
    if (!toolCallId) return;

    const toolName = event?.content?.tool_name;
    const isComplete = !!event?.content?.is_complete;
    const newChunk = event?.content?.text || '';

    setRawMessages((prevMessages) => {
      let didUpdateExisting = false;

      const updatedMessages = prevMessages.map((msg) => {
        const messageToolCallId = msg.tool_call_id || msg.toolCallId;
        const isToolCallMessage = msg.message_type === 'tool_call' || msg.isToolCall;

        if (messageToolCallId === toolCallId && isToolCallMessage) {
          didUpdateExisting = true;

          const previousContent = msg.toolStatus?.streamingContent || '';
          const accumulatedContent = isComplete ? newChunk : previousContent + newChunk;

          return {
            ...msg,
            toolStatus: {
              ...msg.toolStatus,
              streamingContent: accumulatedContent,
              isStreaming: !isComplete,
              status: isComplete ? 'success' : 'streaming',
              toolName: toolName || msg.toolStatus?.toolName,
            }
          };
        }
        return msg;
      });

      if (didUpdateExisting) return updatedMessages;

      const timestamp = new Date(event?.timestamp || Date.now());
      const sender = event?.sub_agent_info?.agent_name ?? 'coding_agent';

      return [
        ...prevMessages,
        {
          sender,
          content: `Calling ${toolName || 'tool'}`,
          timestamp,
          uuid: toolCallId,
          lastUpdated: new Date(),
          isStreaming: false,
          message_type: 'tool_call',
          tool_description: toolName,
          tool_call_id: toolCallId,
          agentType: sender,
          isSubAgent: !!event?.sub_agent_info,
          subAgentInfo: event?.sub_agent_info,
          isToolCall: true,
          toolCallId: toolCallId,
          toolStatus: {
            streamingContent: newChunk,
            isStreaming: !isComplete,
            status: isComplete ? 'success' : 'streaming',
            toolName: toolName || 'Unknown Tool',
          },
        },
      ];
    });
  }, []);

  const handleFixEvent = useCallback((event: any) => {
    const kind = classifyFixEvent(event);
    if (kind === 'chat' || kind === 'delta') {
      handleFixChatMessageEvent(event, kind === 'delta');
    } else if (kind === 'tool_update') {
      handleFixToolStreamingUpdateEvent(event);
    }
  }, [handleFixChatMessageEvent, handleFixToolStreamingUpdateEvent]);

  const fixEventsSocketUrl = fixEventUrl ?? '';

  const fixEventsSocket = useAuthenticatedWebSocket(fixEventsSocketUrl, {
    autoConnect: false,
    onMessage: (data: any) => handleFixEvent(data),
  });

  useEffect(() => {
    if (!fixEventUrl) return;
    fixEventsSocket.connect();

    return () => {
      fixEventsSocket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixEventUrl]);

  const loadFix = useCallback(async (silent = false) => {
    if (!currentOrganization?.id || !fixId) return;

    try {
      if (!silent) {
        setIsLoading(true);
        setError(null);
      }
      const response = await api.get<FixResponse>(
        apiEndpoints.fixes.get(currentOrganization.id, fixId)
      );
      setFix(response);
    } catch (err: any) {
      console.error('Failed to load fix:', err);
      if (!silent) {
        setError(err.message || 'Failed to load fix');
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [api, currentOrganization?.id, fixId]);

  const loadDiff = useCallback(async () => {
    if (!currentOrganization?.id || !fixId) return;

    try {
      setIsDiffLoading(true);
      const response = await api.get<DiffResponse>(
        apiEndpoints.fixes.diff(currentOrganization.id, fixId)
      );
      setDiff(response.diff || null);
    } catch (err: any) {
      console.error('Failed to load diff:', err);
    } finally {
      setIsDiffLoading(false);
    }
  }, [api, currentOrganization?.id, fixId]);

  const handleRetry = async () => {
    if (!currentOrganization?.id || !fixId) return;

    try {
      setIsRetrying(true);
      const response = await api.post<FixResponse>(
        apiEndpoints.fixes.retry(currentOrganization.id, fixId),
        {}
      );
      setFix(response);
      setDiff(null);
      setRightPanelView('activity');
      toast.success('Fix retrying...');
    } catch (err: any) {
      console.error('Failed to retry fix:', err);
      toast.error(err.message || 'Failed to retry fix');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleCancel = async () => {
    if (!currentOrganization?.id || !fixId || !fix) return;

    const confirmed = window.confirm('Stop this fix in progress? Any partial changes in the sandbox will be discarded.');
    if (!confirmed) return;

    try {
      setIsCancelling(true);
      const response = await api.post<FixResponse>(
        apiEndpoints.fixes.cancel(currentOrganization.id, fixId),
        {}
      );
      setFix(response);
      toast.success('Fix stopped');
    } catch (err: any) {
      console.error('Failed to stop fix:', err);
      toast.error(err.message || 'Failed to stop fix');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleRegenerate = async () => {
    if (!currentOrganization?.id || !fixId || !fix) return;

    const openPullRequest = fix.pull_requests?.find((pr) => (pr.merge_status ?? 'open') === 'open');
    if (openPullRequest) {
      const confirmed = window.confirm(
        `Regenerate this fix and update PR #${openPullRequest.pr_number}? This replaces the Kolega-generated branch on the existing PR.`
      );
      if (!confirmed) return;
    }

    try {
      setIsRegenerating(true);
      const response = await api.post<FixResponse>(
        apiEndpoints.fixes.regenerate(currentOrganization.id, fixId),
        {}
      );
      setFix(response);
      setDiff(null);
      setRightPanelView('activity');
      toast.success(openPullRequest ? 'Regenerating fix and updating PR...' : 'Regenerating fix...');
    } catch (err: any) {
      console.error('Failed to regenerate fix:', err);
      toast.error(err.message || 'Failed to regenerate fix');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleRefineSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentOrganization?.id || !fixId || !fix) return;

    const instructions = refinementInstructions.trim();
    if (!instructions) {
      toast.error('Tell the agent what to change');
      return;
    }

    try {
      setIsRefining(true);
      const request: FixRefineRequest = { instructions };
      const response = await api.post<FixResponse>(
        apiEndpoints.fixes.refine(currentOrganization.id, fixId),
        request
      );
      setFix(response);
      setDiff(null);
      setRightPanelView('activity');
      setRefinementInstructions('');
      setIsRefineModalOpen(false);
      const openPullRequest = fix.pull_requests?.find((pr) => (pr.merge_status ?? 'open') === 'open');
      toast.success(openPullRequest ? 'Refining fix and updating PR...' : 'Refining fix...');
    } catch (err: any) {
      console.error('Failed to refine fix:', err);
      toast.error(err.message || 'Failed to refine fix');
    } finally {
      setIsRefining(false);
    }
  };

  useEffect(() => {
    if (!orgLoading && currentOrganization?.id && fixId) {
      loadFix();
    }
  }, [orgLoading, currentOrganization?.id, fixId, loadFix]);

  useEffect(() => {
    // Load diff when fix is completed or failed (diff may exist even if PR creation failed)
    if (
      fix?.status === 'completed' ||
      fix?.status === 'failed' ||
      fix?.status === 'cancelled' ||
      fix?.status === 'pr_creation_failed' ||
      fix?.status === 'pr_update_failed'
    ) {
      loadDiff();
    }
  }, [fix?.status, loadDiff]);

  // Auto-switch to diff view when fix completes and diff is loaded
  useEffect(() => {
    if (fix?.status === 'completed' && diff) {
      setRightPanelView('diff');
    }
  }, [fix?.status, diff]);

  // Credential-based providers (Azure DevOps PAT, AWS CodeCommit keys/role): check the
  // connection is still usable before offering retry / regenerate.
  useEffect(() => {
    if (!fix || !currentOrganization?.id) return;
    const provider = fix.source_repo_provider;
    if (provider !== 'azure_devops' && provider !== 'codecommit') return;

    const orgId = currentOrganization.id;
    const alreadyValidated = sessionStorage.getItem(validationSessionKey(orgId, provider)) === '1';

    if (provider === 'azure_devops') {
      api
        .get<{ connected: boolean; token_expires_at?: string | null; pat_invalid?: boolean }>(
          apiEndpoints.orgAzureDevOps.status(orgId, !alreadyValidated)
        )
        .then((res) => {
          if (res?.connected) {
            if (res.pat_invalid) {
              setIsCredentialsBlocked(true);
            } else if (res.token_expires_at) {
              const expiresAt = new Date(res.token_expires_at);
              setIsCredentialsBlocked(expiresAt < new Date());
            }
          }
        })
        .catch(() => {});
      return;
    }

    const parts = fix.source_repo ? parseCodeCommitFullName(fix.source_repo) : null;
    api
      .get<OrgCodeCommitStatus>(apiEndpoints.orgCodeCommit.status(orgId, !alreadyValidated))
      .then((res) => {
        if (!res?.connected) return;
        const connections = res.connections ?? [];
        const match = parts
          ? connections.find((c) => c.aws_account_id === parts.accountId && c.aws_region === parts.region)
          : undefined;
        // Match the repo's connection when we can; otherwise block if any connection is invalid.
        setIsCredentialsBlocked(match ? !!match.credentials_invalid : connections.some((c) => c.credentials_invalid));
      })
      .catch(() => {});
  }, [fix?.source_repo_provider, fix?.source_repo, fix?.id, currentOrganization?.id, api]);

  // Poll for updates when running or when a regenerated PR branch is being updated
  useEffect(() => {
    if (
      fix?.status === 'running' ||
      fix?.status === 'pending' ||
      fix?.regeneration_pending ||
      fix?.refinement_pending ||
      fix?.pr_update_pending
    ) {
      const interval = setInterval(() => {
        loadFix(true); // Silent poll
      }, 5000); // Poll every 5 seconds
      return () => clearInterval(interval);
    }
  }, [fix?.status, fix?.regeneration_pending, fix?.refinement_pending, fix?.pr_update_pending, loadFix]);

  const handleCreatePR = async () => {
    if (!currentOrganization?.id || !fixId) return;
    if (isDemoOrg) {
      toast.error('Demo repositories are public and read-only. Connect your own repository to create pull requests.');
      return;
    }

    try {
      setIsCreatingPR(true);
      // PR title and body will be auto-generated by LLM on the backend
      const request: CreatePRRequest = {
        title: '', // Auto-generated
        body: '',  // Auto-generated
      };
      const response = await api.post<CreatePRResponse>(
        apiEndpoints.fixes.createPR(currentOrganization.id, fixId),
        request
      );

      if (response.errors.length > 0) {
        toast.error(`Failed to queue PR for ${response.errors.length} repo(s)`);
      } else {
        toast.success('Creating PR... This may take a minute.');
        // Optimistically update local state so the polling effect
        // doesn't see stale pr_creation_pending=false and misfire the error toast
        setFix(prev => prev ? { ...prev, pr_creation_pending: true } : prev);
        // Start polling for PR creation
        startPRPolling();
      }
    } catch (err: any) {
      console.error('Failed to create PR:', err);
      const errorQuotaType = err.data?.detail?.quota_type;
      if (errorQuotaType) {
        setQuotaModalOpen(true);
      } else {
        toast.error(err.message || 'Failed to create pull request');
      }
    } finally {
      setIsCreatingPR(false);
    }
  };

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const startPRPolling = () => {
    setIsPRPending(true);
    
    // Clear any existing polling
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);

    pollIntervalRef.current = setInterval(async () => {
      await loadFix(true); // Silent poll - no loading state
    }, 3000);

    // Stop polling after 2 minutes
    pollTimeoutRef.current = setTimeout(() => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      setIsPRPending(false);
    }, 120000);
  };

  const stopPRPolling = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    setIsPRPending(false);
  };

  // Stop PR polling when PRs are detected or backend signals failure
  useEffect(() => {
    if (!isPRPending) return;
    if (fix?.pull_requests && fix.pull_requests.length > 0) {
      stopPRPolling();
      toast.success(`Created ${fix.pull_requests.length} pull request(s)`);
    } else if (fix && !fix.pr_creation_pending && fix.pull_requests.length === 0) {
      // Backend cleared the flag without creating PRs — PR creation failed
      stopPRPolling();
      toast.error('PR creation failed. Please try again.');
    }
  }, [fix?.pull_requests, fix?.pr_creation_pending, isPRPending]);

  // Restore PR pending state from backend on load/refresh
  const hasRestoredPRPending = useRef(false);
  useEffect(() => {
    if (
      fix?.pr_creation_pending &&
      fix.pull_requests.length === 0 &&
      !isPRPending &&
      !isCreatingPR &&
      !hasRestoredPRPending.current
    ) {
      hasRestoredPRPending.current = true;
      startPRPolling();
    }
  }, [fix?.pr_creation_pending, fix?.pull_requests, isPRPending, isCreatingPR]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  if (orgLoading || isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size={32} />
      </div>
    );
  }

  if (error || !fix) {
    return (
      <div className="h-full flex flex-col px-4 sm:px-6 py-6 sm:py-8">
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-ui-text-secondary hover:text-ui-text-primary mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {backTo.label}
        </button>
        <div className="flex flex-col items-center justify-center flex-1 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-ui-text-secondary">{error || 'Fix not found'}</p>
        </div>
      </div>
    );
  }

  const hasPr = fix.pull_requests && fix.pull_requests.length > 0;
  const prStatus = hasPr ? fix.pull_requests[0]?.merge_status : undefined;
  const prStatusText = prStatus ? prStatusLabel[prStatus] : null;
  const isFixRegenerating = !!fix.regeneration_pending || isRegenerating;
  const isFixRefining = !!fix.refinement_pending || isRefining;
  const canRegenerate =
    ['completed', 'pr_creation_failed', 'pr_update_failed'].includes(fix.status) &&
    prStatus !== 'merged' &&
    prStatus !== 'closed' &&
    !fix.pr_creation_pending &&
    !fix.refinement_pending &&
    !fix.pr_update_pending;
  const canRefine =
    ['completed', 'pr_creation_failed', 'pr_update_failed'].includes(fix.status) &&
    prStatus !== 'merged' &&
    prStatus !== 'closed' &&
    !fix.pr_creation_pending &&
    !fix.regeneration_pending &&
    !fix.refinement_pending &&
    !fix.pr_update_pending;
  const latestRefinement =
    fix.refinement_history && fix.refinement_history.length > 0
      ? fix.refinement_history[fix.refinement_history.length - 1]
      : undefined;
  const canCancel = (fix.status === 'pending' || fix.status === 'running') && !isReadOnly;
  const openPullRequest = fix.pull_requests?.find((pr) => (pr.merge_status ?? 'open') === 'open');
  const config = statusConfig[fix.status];
  const statusLabel = isCancelling
    ? 'Stopping...'
    : fix.refinement_pending
    ? 'Refining...'
    : fix.regeneration_pending
      ? 'Regenerating...'
      : config.label;
  const isStatusSpinning = isCancelling || fix.refinement_pending || fix.regeneration_pending;
  const StatusIcon = config.icon;
  const statusIconClassName = 'w-4 h-4';

  return (
    <>
    <div className="h-full flex flex-col px-4 sm:px-6 py-6 sm:py-8">
      {/* Navigation buttons */}
      <div className="mb-6">
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-ui-text-secondary hover:text-ui-text-primary"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to {backTo.label}
        </button>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0">
        {/* Left column: Main card + Pull Requests */}
        <div className="flex flex-col min-h-0 overflow-y-auto lg:overflow-visible lg:h-full">
          {/* Header card */}
          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-lg flex flex-col lg:h-full lg:min-h-0 lg:overflow-hidden">
            {/* Fixed header — stays in view while the instructions scroll */}
            <div className="shrink-0 p-4 sm:p-6 border-b border-ui-border-primary">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div className="min-w-0 flex-1">
                  <h1 className="text-2xl font-bold text-ui-text-primary mb-2 break-words">
                    {fix.title}
                  </h1>
                  {fix.finding_ids && fix.finding_ids.length > 0 && (
                    <button
                      onClick={() =>
                        navigate(`/repositories/${routeApplicationId}/findings/${fix.finding_ids[0]}`, {
                          state: fixOrigin,
                        })
                      }
                      className="inline-flex items-center gap-1.5 text-sm text-brand-primary hover:underline mt-1"
                    >
                      <Bug className="w-4 h-4" />
                      View finding
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full whitespace-nowrap ${config.bgColor} ${config.color}`}>
                    {isStatusSpinning ? (
                      <Spinner size={16} tone="current" />
                    ) : (
                      <StatusIcon className={statusIconClassName} />
                    )}
                    <span className="text-sm font-medium">{statusLabel}</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Scrollable body — only this scrolls; the header above stays in view */}
            <div className="px-4 sm:px-6 pb-4 sm:pb-6 lg:flex-1 lg:min-h-0 lg:overflow-y-auto">

            {(fix.status === 'failed' || canRegenerate) && isCredentialsBlocked && (
              <div className="mt-4 flex items-center gap-2 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg text-sm text-orange-800 dark:text-orange-200">
                <AlertCircle className="w-4 h-4 shrink-0 text-orange-500" />
                <span>
                  {fix.source_repo_provider === 'codecommit'
                    ? `Your ${providerLabel('codecommit')} credentials are invalid or revoked.`
                    : 'Your Azure DevOps PAT has expired.'}{' '}
                  Retry and regeneration are disabled until you{' '}
                  <button
                    onClick={() => openOrgSettings(currentOrganization!.id, 'integrations')}
                    className="font-medium underline hover:no-underline"
                  >
                    reconnect your integration
                  </button>
                  .
                </span>
              </div>
            )}

            {fix.error_message && (
              <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <h3 className="text-sm font-medium text-red-700 dark:text-red-400 mb-2">
                  Error
                </h3>
                <p className="text-red-600 dark:text-red-400 text-sm whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                  {fix.error_message}
                </p>
                {fix.status === 'pr_creation_failed' && (
                  <p className="text-amber-600 dark:text-amber-400 text-xs mt-2">
                    The fix was generated successfully — only PR creation failed. Use{' '}
                    <strong>Retry PR</strong> in the toolbar below to try again, or{' '}
                    <strong>Regenerate</strong> to start over.
                  </p>
                )}
                {fix.status === 'pr_update_failed' && (
                  <p className="text-amber-600 dark:text-amber-400 text-xs mt-2">
                    The fix was regenerated, but the existing PR branch could not be updated.
                    You can regenerate again after resolving the issue.
                  </p>
                )}
              </div>
            )}

            <div className="mt-4 p-4 bg-ui-background-tertiary rounded-lg">
              <h3 className="text-sm font-medium text-ui-text-secondary mb-2">
                Instructions
              </h3>
              <p className="text-ui-text-secondary whitespace-pre-wrap">
                {fix.instructions}
              </p>
            </div>

            {latestRefinement && (
              <div className="mt-3 p-4 bg-ui-background-secondary border border-ui-border-primary rounded-lg">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <h3 className="text-sm font-medium text-ui-text-secondary">
                    Latest refinement
                  </h3>
                  <span className="text-xs text-ui-text-tertiary">
                    Attempt {latestRefinement.attempt} - {new Date(latestRefinement.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-ui-text-secondary whitespace-pre-wrap text-sm">
                  {latestRefinement.instructions}
                </p>
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ui-text-tertiary">
              <span>Created {new Date(fix.created_at).toLocaleString()}</span>
              {fix.completed_at && (
                <span>Completed {new Date(fix.completed_at).toLocaleString()}</span>
              )}
              {fix.last_regenerated_at && (
                <span>Regenerated {new Date(fix.last_regenerated_at).toLocaleString()}</span>
              )}
              {fix.last_refined_at && (
                <span>Refined {new Date(fix.last_refined_at).toLocaleString()}</span>
              )}
            </div>
            </div>
          </div>
        </div>

        {/* Right column: Agent Activity / Diff toggle */}
        <div className="flex flex-col min-h-[500px] lg:min-h-0 lg:h-full">
          <div className="bg-ui-background-secondary border border-ui-border-primary rounded-lg flex-1 flex flex-col overflow-hidden">
            {/* Header with toggle tabs */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b border-ui-border-primary">
              {/* Left: Toggle tabs + Status indicator */}
              <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto">
                {diff ? (
                  <div className="flex gap-1">
                    <button
                      onClick={() => setRightPanelView('activity')}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        rightPanelView === 'activity'
                          ? 'bg-brand-primary text-black font-medium'
                          : 'text-ui-text-tertiary hover:bg-ui-background-tertiary'
                      }`}
                    >
                      Activity
                    </button>
                    <button
                      onClick={() => setRightPanelView('diff')}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        rightPanelView === 'diff'
                          ? 'bg-brand-primary text-black font-medium'
                          : 'text-ui-text-tertiary hover:bg-ui-background-tertiary'
                      }`}
                    >
                      Changes
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-ui-text-secondary" />
                    <h2 className="text-lg font-semibold text-ui-text-primary">
                      Activity
                    </h2>
                  </div>
                )}
                {rightPanelView === 'activity' && (fix.status === 'running' || fix.status === 'pending') && (
                  <div className="flex items-center gap-2">
                    <Spinner size={16} tone="current" className="text-amber-600 dark:text-amber-400" />
                    <span className="text-sm text-ui-text-tertiary">
                      {fix.refinement_pending
                        ? (fix.status === 'pending' ? 'Preparing refinement...' : 'Refining...')
                        : fix.regeneration_pending
                        ? (fix.status === 'pending' ? 'Preparing regeneration...' : 'Regenerating...')
                        : (fix.status === 'pending' ? 'Waiting to start...' : 'Working...')}
                    </span>
                  </div>
                )}
              </div>

            </div>

            {/* Content area */}
            <div className="flex-1 overflow-hidden">
              {rightPanelView === 'activity' ? (
                /* Agent Activity content */
                <div
                  ref={messagesContainerRef}
                  className="h-full overflow-y-auto"
                  onScroll={autoScroll.enhancedHandleScroll}
                  onWheel={autoScroll.handleWheelScroll}
                  onTouchStart={autoScroll.handleScrollStart}
                  onTouchMove={autoScroll.handleScrollStart}
                >
                  {messages.length > 0 ? (
                    <MessageList
                      validMessages={messages}
                      copiedMessageId={copiedMessageId}
                      copyToClipboard={copyToClipboard}
                      expandedTools={expandedTools}
                      toggleToolExpansion={toggleToolExpansion}
                      agentStatus={fix.status === 'running' ? 'generating' : 'stopped'}
                      searchQuery=""
                      activeSearchResult={null}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-ui-text-tertiary">
                      <p>No agent activity recorded yet.</p>
                    </div>
                  )}
                  <ScrollToBottomButton
                    isNearBottom={autoScroll.isNearBottom}
                    scrollToBottom={autoScroll.scrollToBottom}
                  />
                </div>
              ) : (
                /* Diff content */
                <div className="h-full overflow-y-auto p-4">
                  {isDiffLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner size={24} />
                    </div>
                  ) : diff ? (
                    <DiffViewer diff={diff} />
                  ) : (
                    <p className="text-ui-text-tertiary text-center py-8">
                      No changes detected
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <FixActionToolbar
        fix={fix}
        hasDiff={!!diff}
        isReadOnly={isReadOnly}
        isDemoOrg={isDemoOrg}
        isCredentialsBlocked={isCredentialsBlocked}
        isCancelling={isCancelling}
        isRetrying={isRetrying}
        isFixRegenerating={isFixRegenerating}
        isFixRefining={isFixRefining}
        isCreatingPR={isCreatingPR}
        isPRPending={isPRPending}
        canCancel={canCancel}
        canRegenerate={canRegenerate}
        canRefine={canRefine}
        isFreeTierNoPRs={isFreeTierNoPRs}
        prQuotaExhausted={prQuotaExhausted}
        prStatusText={prStatusText}
        onCancel={handleCancel}
        onRetry={handleRetry}
        onRegenerate={handleRegenerate}
        onRefine={() => setIsRefineModalOpen(true)}
        onCreatePR={handleCreatePR}
        onUpgrade={() => setQuotaModalOpen(true)}
        onBack={goBack}
        backLabel={backTo.label}
      />
    </div>

    {isRefineModalOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
        <div className="w-full max-w-lg rounded-lg border border-ui-border-primary bg-ui-background-secondary shadow-xl">
          <form onSubmit={handleRefineSubmit}>
            <div className="p-5 border-b border-ui-border-primary">
              <h2 className="text-lg font-semibold text-ui-text-primary">
                Refine fix
              </h2>
              {openPullRequest && (
                <p className="mt-2 text-sm text-ui-text-secondary">
                  The refined patch will update PR #{openPullRequest.pr_number} after generation finishes.
                </p>
              )}
            </div>
            <div className="p-5">
              <label htmlFor="refinement-instructions" className="block text-sm font-medium text-ui-text-secondary mb-2">
                Tell the agent what to change
              </label>
              <textarea
                id="refinement-instructions"
                value={refinementInstructions}
                onChange={(event) => setRefinementInstructions(event.target.value)}
                rows={6}
                autoFocus
                placeholder="Example: Keep the validation stricter, but avoid changing the public API."
                className="w-full rounded-lg border border-ui-border-primary bg-ui-background-primary px-3 py-2 text-sm text-ui-text-primary placeholder:text-ui-text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-ui-border-primary">
              <button
                type="button"
                onClick={() => {
                  setIsRefineModalOpen(false);
                  setRefinementInstructions('');
                }}
                disabled={isRefining}
                className="px-4 py-2 text-sm font-medium text-ui-text-secondary hover:text-ui-text-primary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isRefining || !refinementInstructions.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-medium text-black hover:bg-brand-primaryDark disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRefining && <Spinner size={16} tone="current" />}
                Refine fix
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    <QuotaExceededModal
      isOpen={quotaModalOpen}
      onClose={() => setQuotaModalOpen(false)}
      quotaType="prs"
      isFreeTier={accessType === 'free'}
    />
    </>
  );
};

export default FixDetailPage;
